<template>
  <nav v-if="totalPages > 1" class="app-pagination" aria-label="Navigasi Halaman">
    <ul class="pagination pagination-sm mb-0 gap-1 align-items-center">
      <!-- Tombol Sebelumnya -->
      <li class="page-item" :class="{ disabled: !canPrev }">
        <button
          type="button"
          class="page-link rounded"
          :disabled="!canPrev"
          @click="onPageClick(currentPage - 1)"
          aria-label="Halaman Sebelumnya"
        >
          <i class="bi bi-chevron-left" aria-hidden="true"></i>
        </button>
      </li>

      <!-- Daftar Nomor Halaman -->
      <li
        v-for="(p, idx) in visiblePages"
        :key="idx"
        class="page-item"
        :class="{ active: p === currentPage, disabled: p === '...' }"
      >
        <span v-if="p === '...'" class="page-link page-link--dots border-0">...</span>
        <button
          v-else
          type="button"
          class="page-link rounded"
          :class="{ 'fw-bold': p === currentPage }"
          @click="onPageClick(p)"
        >
          {{ p }}
        </button>
      </li>

      <!-- Tombol Berikutnya -->
      <li class="page-item" :class="{ disabled: !canNext }">
        <button
          type="button"
          class="page-link rounded"
          :disabled="!canNext"
          @click="onPageClick(currentPage + 1)"
          aria-label="Halaman Berikutnya"
        >
          <i class="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
export default {
  name: 'AppPagination',
  props: {
    currentPage: {
      type: Number,
      required: true,
      default: 1,
    },
    totalPages: {
      type: Number,
      required: true,
      default: 1,
    },
    prevPageUrl: {
      type: [String, Boolean, Object],
      default: null,
    },
    nextPageUrl: {
      type: [String, Boolean, Object],
      default: null,
    },
  },
  emits: ['change-page'],
  computed: {
    canPrev() {
      if (this.prevPageUrl !== null) return Boolean(this.prevPageUrl) && this.currentPage > 1;
      return this.currentPage > 1;
    },
    canNext() {
      if (this.nextPageUrl !== null) return Boolean(this.nextPageUrl) && this.currentPage < this.totalPages;
      return this.currentPage < this.totalPages;
    },
    visiblePages() {
      const total = Number(this.totalPages) || 1;
      const current = Number(this.currentPage) || 1;

      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      // Format seperti: 1 2 3 ... 89 90 saat di awal
      if (current <= 2) {
        return [1, 2, 3, '...', total - 1, total];
      }
      if (current === 3) {
        return [1, 2, 3, 4, '...', total - 1, total];
      }

      // Format saat di akhir halaman (misal halaman 89 atau 90 dari 90)
      if (current >= total - 1) {
        return [1, 2, '...', total - 2, total - 1, total];
      }
      if (current === total - 2) {
        return [1, 2, '...', total - 3, total - 2, total - 1, total];
      }

      // Format di tengah-tengah
      return [1, '...', current - 1, current, current + 1, '...', total];
    },
  },
  methods: {
    onPageClick(page) {
      if (page === '...' || page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }
      this.$emit('change-page', page);
    },
  },
};
</script>

<style scoped>
.app-pagination .page-link {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: var(--text-base, #333);
  background-color: var(--bg-surface, #fff);
  border-color: var(--border-soft, #dee2e6);
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

@media (hover: none) and (pointer: coarse), (max-width: 768px) {
  .app-pagination .page-link {
    min-width: 42px;
    height: 42px;
    font-size: 0.9rem;
  }
}

.app-pagination .page-link:hover:not(:disabled) {
  background-color: var(--bg-subtle, #f1f5f9);
  color: var(--color-primary, #0d6efd);
}

.app-pagination .page-item.active .page-link {
  background-color: var(--color-primary, #0d6efd);
  border-color: var(--color-primary, #0d6efd);
  color: #fff;
}

.app-pagination .page-link--dots {
  background: transparent !important;
  color: var(--text-muted, #94a3b8);
  cursor: default;
}
</style>
